<template>
  <div class="video-creation-polling">
    <!-- Polling Status -->
    <div class="polling-status">
      <div class="status-card">
        <!-- Loading/Processing State -->
        <div
          v-if="polling.loading || polling.status === 'PROCESSING'"
          class="status-loading"
        >
          <div class="hourglass-container">
            <svg
              width="73px"
              height="88px"
              viewBox="0 0 73 88"
              version="1.1"
              xmlns="http://www.w3.org/2000/svg"
              xmlns:xlink="http://www.w3.org/1999/xlink"
            >
              <g id="hourglass">
                <path
                  id="glass"
                  d="M63.8761664,86 C63.9491436,84.74063 64,83.4707791 64,82.1818182 C64,65.2090455 57.5148507,50.6237818 48.20041,44 C57.5148507,37.3762182 64,22.7909545 64,5.81818182 C64,4.52922091 63.9491436,3.25937 63.8761664,2 L10.1238336,2 C10.0508564,3.25937 10,4.52922091 10,5.81818182 C10,22.7909545 16.4851493,37.3762182 25.79959,44 C16.4851493,50.6237818 10,65.2090455 10,82.1818182 C10,83.4707791 10.0508564,84.74063 10.1238336,86 L63.8761664,86 Z"
                  fill="#ECF1F6"
                />
                <rect
                  id="top-plate"
                  fill="#4D4544"
                  x="0"
                  y="0"
                  width="74"
                  height="8"
                  rx="2"
                />
                <rect
                  id="bottom-plate"
                  fill="#4D4544"
                  x="0"
                  y="80"
                  width="74"
                  height="8"
                  rx="2"
                />

                <g
                  id="top-sand"
                  transform="translate(18, 21)"
                >
                  <clipPath
                    id="top-clip-path"
                    fill="white"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="38"
                      height="21"
                    />
                  </clipPath>

                  <path
                    fill="#F5A623"
                    clip-path="url(#top-clip-path)"
                    d="M38,0 C36.218769,7.51704545 24.818769,21 19,21 C13.418769,21 1.9,7.63636364 0,0 L38,0 Z"
                  />
                </g>

                <g
                  id="bottom-sand"
                  transform="translate(18, 55)"
                >
                  <clipPath
                    id="bottom-clip-path"
                    fill="white"
                  >
                    <rect
                      x="0"
                      y="0"
                      width="38"
                      height="21"
                    />
                  </clipPath>

                  <g clip-path="url(#bottom-clip-path)">
                    <path
                      fill="#F5A623"
                      d="M0,21 L38,21 C36.1,13.3636364 24.581231,0 19,0 C13.181231,0 1.781231,13.4829545 0,21 Z"
                    />
                  </g>
                </g>
              </g>
            </svg>
          </div>
          <div class="loading-content">
            <h4 class="loading-title">
              {{ polling.loading ? 'Checking Status...' : 'Processing Video...' }}
            </h4>
            <p class="loading-description">
              {{ polling.loading ? 'Please wait while we check the progress of your video generation.' : 'Your video is being generated. This may take a few minutes.' }}
            </p>
          </div>
        </div>

        <!-- Error State -->
        <div
          v-else-if="polling.error"
          class="status-error"
        >
          <feather-icon
            icon="AlertCircleIcon"
            size="24"
            class="error-icon"
          />
          <div class="error-content">
            <h4 class="error-title">
              Error Occurred
            </h4>
            <p class="error-description">
              {{ polling.error }}
            </p>
            <b-button
              variant="outline-primary"
              size="sm"
              @click="retryPolling"
            >
              <feather-icon
                icon="RefreshCwIcon"
                size="14"
              />
              Retry
            </b-button>
          </div>
        </div>

        <!-- Success State -->
        <div
          v-else-if="polling.status === 'SUCCESS' && polling.videoUrl"
          class="status-success"
        >
          <div class="success-content">
            <p class="success-description">
              Your video has been generated successfully.
            </p>

            <!-- Video Preview -->
            <div class="video-preview">
              <video
                :src="polling.videoUrl"
                controls
                class="generated-video"
                preload="metadata"
              >
                Your browser does not support the video tag.
              </video>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex'
import { BButton } from 'bootstrap-vue'

export default {
  name: 'VideoCreationPolling',
  components: {
    BButton,
  },
  props: {
    taskId: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      polling: {
        loading: false,
        error: '',
        status: null,
        videoUrl: null,
        pollingInterval: null,
      },
    }
  },
  mounted() {
    this.startPolling()
  },
  beforeDestroy() {
    this.stopPolling()
  },
  methods: {
    ...mapActions('videoGeneration', [
      'pollVideoStatus',
    ]),

    startPolling() {
      if (!this.taskId) {
        this.polling.error = 'No task ID provided'
        return
      }

      this.polling.loading = true
      this.polling.error = ''

      // Start polling immediately
      this.checkStatus()

      // Set up interval polling
      this.polling.pollingInterval = setInterval(() => {
        this.checkStatus()
      }, 5000) // Poll every 5 seconds
    },

    async checkStatus() {
      try {
        const result = await this.pollVideoStatus({ taskIds: [this.taskId] })

        if (result.success) {
          this.polling.loading = false
          this.polling.error = ''

          // Extract status and video URL from the response
          if (result.data && result.data.length > 0) {
            const batches = Array.isArray(result.data) ? result.data : []
            const items = batches.flat().filter(Boolean)

            // Derive aggregate status across all items
            const hasError = items.some(item => ['FAILED', 'ERROR'].includes(item.status))
            const allSuccess = items.length > 0 && items.every(item => item.status === 'SUCCESS')
            const anyProcessing = items.some(item => item.status === 'PROCESSING')
            const previewItem = items.find(item => item.preview_url)

            if (hasError) {
              this.polling.status = 'FAILED'
              this.polling.error = 'One or more tasks failed'
              this.stopPolling()
              return
            }

            if (allSuccess && previewItem && previewItem.preview_url) {
              this.polling.status = 'SUCCESS'
              this.polling.videoUrl = previewItem.preview_url
              this.stopPolling()
              this.proceedToNext()
              return
            }

            // Still processing
            if (anyProcessing) {
              this.polling.status = 'PROCESSING'
            }
          }
        } else {
          this.polling.loading = false
          this.polling.error = result.error?.message || 'Failed to check status'
        }
      } catch (error) {
        this.polling.loading = false
        this.polling.error = error.message || 'Unknown error occurred'
      }
    },

    proceedToNext() {
      this.$emit('proceed-to-next')
    },

    stopPolling() {
      if (this.polling.pollingInterval) {
        clearInterval(this.polling.pollingInterval)
        this.polling.pollingInterval = null
      }
    },

    retryPolling() {
      this.polling.error = ''
      this.startPolling()
    },

  },
}
</script>

<style lang="scss" scoped>
.video-creation-polling {
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 24px;
  min-width: 800px;
  margin: 0 auto;

  .polling-status {
    .status-card {
      background: #ffffff;
      border: 1px solid #e9ecef;
      border-radius: 12px;
      padding: 24px;
      min-height: 400px;
      display: flex;
      align-items: center;
      justify-content: center;

      .status-loading,
      .status-error,
      .status-success {
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        gap: 16px;
        width: 100%;
      }

      .status-loading {
        .hourglass-container {
          margin-bottom: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
        }

        .loading-content {
          .loading-title {
            font-size: 20px;
            font-weight: 600;
            color: #2c3e50;
            margin: 0 0 8px 0;
            text-align: center;
          }

          .loading-description {
            color: #6c757d;
            margin: 0;
            text-align: center;
            font-size: 16px;
          }
        }
      }

      .status-error {
        .error-icon {
          color: #dc3545;
        }

        .error-content {
          .error-title {
            font-size: 18px;
            font-weight: 600;
            color: #dc3545;
            margin: 0 0 8px 0;
          }

          .error-description {
            color: #6c757d;
            margin: 0 0 16px 0;
          }
        }
      }

      .status-success {
        .success-icon {
          color: #28a745;
        }

        .success-content {
          .success-title {
            font-size: 18px;
            font-weight: 600;
            color: #28a745;
            margin: 0 0 8px 0;
          }

          .success-description {
            color: #6c757d;
            margin: 0 0 20px 0;
          }

          .video-preview {
            margin: 20px 0;

            .generated-video {
              max-width: 100%;
              max-height: 500px;
              border-radius: 8px;
              background: #000;
            }
          }

        }
      }

    }
  }
}

/* SVG Hourglass Animations */
@keyframes top-clip {
  0% {}
  50% { transform: translateY(21px); }
  100% { transform: translateY(21px); }
}

@keyframes bottom-sand-path {
  0% {}
  50% { transform: translateY(0); }
  100% { transform: translateY(0); }
}

@keyframes bottom-sand-g {
  0% {}
  85% { transform: translateY(0); }
  100% { transform: translateY(-9px); }
}

@keyframes hourglass-rotation {
  50% { transform: rotateZ(0); }
  100% { transform: rotateZ(180deg); }
}

/* Apply animations to SVG elements */
#top-sand #top-clip-path rect,
#bottom-sand path,
#bottom-sand g,
svg {
  animation-duration: 5s;
  animation-delay: 1s;
  animation-iteration-count: infinite;
}

#top-sand #top-clip-path rect {
  animation-name: top-clip;
}

#bottom-sand path {
  transform: translateY(21px);
  animation-name: bottom-sand-path;
}

#bottom-sand g {
  animation-name: bottom-sand-g;
}

svg {
  animation-name: hourglass-rotation;
}
</style>
